import type { SapBondStatusData } from '@/types/approval';

/** SAP 채권현황(185) — 브랜드·연월로 SAP 채권현황을 조회해 표 HTML 과 원문을 함께 저장 */
export const SAP_BOND_STATUS_TYPE = 185;

export const isSapBondStatusType = (apvType: number): boolean =>
  apvType === SAP_BOND_STATUS_TYPE;

/** 레거시 진입 기본 브랜드 */
export const SAP_BOND_STATUS_DEFAULT_BRAND = 'M';

export const SAP_BOND_STATUS_LOADING_MESSAGE =
  '데이터를 불러오는 중입니다. 잠시 후 다시 시도해주세요.';
export const SAP_BOND_STATUS_NOT_LOADED_MESSAGE =
  '데이터를 로드하지 않았습니다. 조회 조건 선택 후 확인 버튼을 누르세요.';

/** 마지막으로 조회한 조건과 결과. 조회 실패면 data 가 null */
export type SapBondStatusState = {
  year: number;
  month: number;
  brandCode: string;
  brandName: string;
  data: SapBondStatusData | null;
  /** 응답 대기 중 — 이때 저장하면 표 없이 저장됨 */
  loading?: boolean;
  /** 「확인」으로 직접 조회함. 진입 자동 조회는 미저장 경고 대상이 아님 */
  userQueried?: boolean;
  /** 진입 때 보인 조회 조건(자동 조회·편집 복원). 같은 조건 재조회는 미저장 경고 대상이 아님 */
  entryCondition?: string;
  /** 진입 때 표가 있었는지. 없다가 새로 받으면 같은 조건이어도 미저장 경고 대상 */
  entryHasData?: boolean;
};

export const sapBondStatusCondition = (
  state: Pick<SapBondStatusState, 'brandCode' | 'year' | 'month'>,
): string => JSON.stringify([state.brandCode, state.year, state.month]);

export const formatSapBondStatusPeriod = (year: number, month: number) =>
  `${year}-${String(month).padStart(2, '0')}`;

const toInt = (v: unknown): number | null => {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isInteger(n) ? n : null;
};

/** 저장 attributes → 상태. 연·월이 없으면 null */
export const parseSapBondStatusAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): SapBondStatusState | null => {
  const year = toInt(attributes?.year);
  const month = toInt(attributes?.month);
  if (!attributes || !year || !month) return null;
  const { data } = attributes;
  return {
    year,
    month,
    brandCode:
      typeof attributes.brandCode === 'string' ? attributes.brandCode : '',
    brandName: '',
    data:
      data && typeof data === 'object' && Object.keys(data).length
        ? (data as SapBondStatusData)
        : null,
  };
};

export const parseSapBondStatusContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => {
  const content = attributes?.content;
  return typeof content === 'string' && content.trim() ? content : null;
};

/** 조회 실패 후 상신은 레거시처럼 허용, 조회 전·조회 중만 막음 */
export const checkSapBondStatusBeforeSave = (
  state: SapBondStatusState | null | undefined,
): string | null => {
  if (!state) return SAP_BOND_STATUS_NOT_LOADED_MESSAGE;
  if (state.loading) return SAP_BOND_STATUS_LOADING_MESSAGE;
  return null;
};

export const sapBondStatusKey = (
  state: SapBondStatusState | null | undefined,
): string => {
  if (!state?.userQueried) return '';
  const condition = sapBondStatusCondition(state);
  if (condition !== state.entryCondition) return condition;
  return !state.entryHasData && state.data ? `${condition}|data` : '';
};

/** 저장 attributes — content 는 작성 화면에 보인 표 HTML */
export const buildSapBondStatusAttributes = (
  state: SapBondStatusState | null | undefined,
  content: string,
) =>
  state
    ? {
        content,
        year: state.year,
        month: state.month,
        brandCode: state.brandCode,
        data: state.data ?? {},
      }
    : null;
